import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { updateQuestion } from "../../api/mathBank";
import { getAiProcessJob, startAiProcessJob } from "../../api/documentProcessing";
import { questionDifficulties } from "../document-processing/mathBankQuestionOptions";
import QuestionAssetPreviewStrip from "../document-processing/QuestionAssetPreviewStrip";
import MarkdownMathText from "./MarkdownMathText";
import { collectQuestionPromptImages } from "../../features/document-processing/questionSolutionImages";

const pollInterval = process.env.NODE_ENV === "test" ? 0 : 1200;
const editableFields = [
  ["prompt_md", "題幹", 6],
  ["answer_md", "答案", 2],
  ["solution_md", "詳解", 6],
];

const formValues = (question) => ({
  prompt_md: question?.prompt_md || "",
  answer_md: question?.answer_md || "",
  solution_md: question?.solution_md || "",
  difficulty: question?.difficulty || "U",
  change_summary: question?.variant_parameters?.change_summary || "",
});

// Keep the same section format used by the question admin's AI solution parser.
export const parseVariantSolutionText = (value) => {
  const text = String(value || "").trim()
    .replace(/^```(?:markdown|md)?\s*\n?/i, "")
    .replace(/\n?```\s*$/i, "").trim();
  const sectionPattern = /(?:^|\n)\s*(?:#{1,6}\s*)?(?:\*\*)?(題目|解題思路|詳解|解題過程|計算過程|過程|解題步驟|計算步驟|步驟|檢查|答案)(?:\*\*)?\s*[：:](?:\*\*)?\s*/g;
  const matches = [...text.matchAll(sectionPattern)];
  const sections = {};
  matches.forEach((match, index) => {
    const start = (match.index || 0) + match[0].length;
    sections[match[1]] = text.slice(start, matches[index + 1]?.index ?? text.length).trim();
  });
  const parts = [];
  if (sections.解題思路) parts.push(`**解題思路**\n\n${sections.解題思路}`);
  const detail = sections.詳解 || sections.解題過程 || sections.計算過程 || sections.過程 || sections.解題步驟 || sections.計算步驟 || sections.步驟;
  if (detail) parts.push(detail);
  if (sections.檢查) parts.push(`**檢查**\n\n${sections.檢查}`);
  return { solution_md: parts.join("\n\n").trim() || text, answer_md: sections.答案 || "" };
};

export default function QuestionVariantEditor({ subject, authToken, question, onSaved, onUse, onClose }) {
  const [form, setForm] = useState(() => formValues(question));
  const [savedStatus, setSavedStatus] = useState(question?.status || "draft");
  const [reviewed, setReviewed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const active = useRef(false);
  const scope = useRef(0);
  const aiRequest = useRef(0);
  const saveRequest = useRef(0);
  const operationLock = useRef(null);
  const wait = useRef(null);
  const baseQuestion = useRef(question);

  const cancelWait = () => {
    if (!wait.current) return;
    clearTimeout(wait.current.timer);
    wait.current.resolve(false);
    wait.current = null;
  };

  useEffect(() => {
    active.current = true;
    scope.current += 1;
    aiRequest.current += 1;
    saveRequest.current += 1;
    operationLock.current = null;
    cancelWait();
    baseQuestion.current = question;
    setForm(formValues(question));
    setSavedStatus(question?.status || "draft");
    setReviewed(false);
    setSaving(false);
    setAiBusy(false);
    setMessage(null);
    return () => {
      active.current = false;
      scope.current += 1;
      aiRequest.current += 1;
      saveRequest.current += 1;
      operationLock.current = null;
      cancelWait();
    };
    // Same-question parent refreshes must not overwrite an unsaved edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject, authToken, question?.id]);

  const change = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setReviewed(false);
    setMessage(null);
    if (editableFields.some(([name]) => name === field) && operationLock.current === "ai") {
      aiRequest.current += 1;
      operationLock.current = null;
      cancelWait();
      setAiBusy(false);
      setMessage({ text: "內容已變更；先前的 AI 計算結果不會套用，請重新計算。" });
    }
  };

  const close = () => {
    active.current = false;
    scope.current += 1;
    aiRequest.current += 1;
    saveRequest.current += 1;
    operationLock.current = null;
    cancelWait();
    onClose?.();
  };

  const complete = editableFields.every(([name]) => String(form[name]).trim());
  const canSave = Boolean(question?.id && authToken && String(form.prompt_md).trim());
  let hasPromptImage = false;
  let promptImageError = "";
  try {
    hasPromptImage = collectQuestionPromptImages({ prompt: form.prompt_md, assets: question?.assets || [] }).length > 0;
  } catch (error) {
    hasPromptImage = true;
    promptImageError = error.message;
  }
  const busy = saving || aiBusy;

  const save = async (useAfterSaving) => {
    if (operationLock.current || !canSave || (useAfterSaving && (!complete || !reviewed))) return;
    operationLock.current = "save";
    const requestId = ++saveRequest.current;
    const scopeId = scope.current;
    const isCurrent = () => active.current && scope.current === scopeId && saveRequest.current === requestId;
    const original = baseQuestion.current;
    const parameters = original?.variant_parameters && typeof original.variant_parameters === "object" && !Array.isArray(original.variant_parameters)
      ? original.variant_parameters : {};
    const data = {
      prompt_md: form.prompt_md,
      answer_md: form.answer_md,
      solution_md: form.solution_md,
      difficulty: form.difficulty,
      variant_parameters: { ...parameters, change_summary: form.change_summary },
      status: useAfterSaving ? "published" : savedStatus,
    };
    setSaving(true);
    setMessage(null);
    try {
      const result = await updateQuestion({ subject, authToken, id: original.id, data });
      if (!isCurrent()) return;
      const saved = { ...original, ...data, ...result };
      baseQuestion.current = saved;
      setSavedStatus(saved.status);
      setMessage({ text: useAfterSaving ? "題目已確認並儲存。" : saved.status === "draft" ? "草稿已儲存，可以稍後再核對。" : "題目修改已儲存。" });
      onSaved?.(saved);
      if (useAfterSaving && isCurrent()) onUse?.(saved);
    } catch (error) {
      if (isCurrent()) setMessage({ error: true, text: error.message || "儲存題目失敗，請重試。" });
    } finally {
      if (isCurrent()) { operationLock.current = null; setSaving(false); }
    }
  };

  const recalculate = async () => {
    if (operationLock.current || !canSave || hasPromptImage) return;
    operationLock.current = "ai";
    const requestId = ++aiRequest.current;
    const scopeId = scope.current;
    const isCurrent = () => active.current && scope.current === scopeId && aiRequest.current === requestId;
    const pause = () => new Promise((resolve) => {
      const timer = setTimeout(() => { wait.current = null; resolve(true); }, pollInterval);
      wait.current = { timer, resolve };
    });
    setAiBusy(true);
    setReviewed(false);
    setMessage(null);
    try {
      // Recalculate from the edited prompt; a previous answer may belong to old numbers.
      const result = await startAiProcessJob({ text: `題目：\n${form.prompt_md.trim()}`, workflow: "solution", authToken });
      if (!isCurrent()) return;
      if (!result?.success || !result.job?.id) throw new Error(result?.error || "AI 計算工作建立失敗，請重試。");
      let job = result.job;
      let transientFailures = 0;
      let polls = 0;
      while (!["success", "error"].includes(job.status)) {
        if (++polls > 250) throw new Error("AI 計算等待逾時，請稍後重新計算。");
        const waited = await pause();
        if (!waited || !isCurrent()) return;
        const progress = await getAiProcessJob({ jobId: job.id, authToken });
        if (!isCurrent()) return;
        if (!progress?.success || !progress.job) {
          if (progress?.status === 0 && ++transientFailures < 3) continue;
          throw new Error(progress?.error || "無法取得 AI 計算結果，請重試。");
        }
        transientFailures = 0;
        job = progress.job;
      }
      if (job.status !== "success" || !String(job.text || "").trim()) throw new Error(job.error || "AI 未回傳可用的計算內容，請重試。");
      const parsed = parseVariantSolutionText(job.text);
      if (!isCurrent()) return;
      setForm((current) => ({ ...current, solution_md: parsed.solution_md, answer_md: parsed.answer_md || current.answer_md }));
      setReviewed(false);
      setMessage({ text: parsed.answer_md
        ? "AI 已更新答案與詳解，尚未儲存；請核對數字、計算與難度。"
        : "AI 已填入詳解，尚未儲存；未辨識到獨立答案，請核對並自行更新答案。" });
    } catch (error) {
      if (isCurrent()) setMessage({ error: true, text: error.message || "AI 重新計算失敗，請重試。" });
    } finally {
      if (isCurrent()) { operationLock.current = null; setAiBusy(false); }
    }
  };

  if (!question) return null;
  return <Editor aria-label="變式題編輯" aria-busy={busy}>
    <Header><div><h3>編輯並核對題目</h3><p>{savedStatus === "draft" ? "已存入題庫草稿" : "已發布題目"} · 修改後可保留或用來換題</p></div>{onClose && <Button type="button" onClick={close}>返回換題</Button>}</Header>
    <Hint>改數字可能改變難度；請一併核對答案、詳解與難度。AI 重新計算只會填入編輯欄位，確認後才會儲存。</Hint>
    {hasPromptImage && <Hint role="note">{promptImageError ? `${promptImageError}AI 重新計算已停用。` : "這題含有題幹圖片；目前的 AI 重新計算只讀文字，因此已停用。"}文字編輯不會更新圖片，請連同題圖人工核對條件、答案與詳解。</Hint>}
    {message && <Message role={message.error ? "alert" : "status"} $error={message.error}>{message.text}</Message>}
    {aiBusy && <Message role="status">AI 正在重新計算；你可以繼續編輯，內容變更後會忽略這次結果。</Message>}
    <Fields>{editableFields.map(([name, label, rows]) => <Field key={name}>
      <label>{label}<textarea aria-label={`變式${label}`} rows={rows} value={form[name]} disabled={saving} onChange={(event) => change(name, event.target.value)} /></label>
      <Preview aria-label={`${label}預覽`}><strong>{label}預覽</strong>{form[name].trim() ? <MarkdownMathText source={form[name]} /> : <span>尚未填寫{label}</span>}<QuestionAssetPreviewStrip assets={question.assets || []} role={name === "prompt_md" ? "prompt" : name === "answer_md" ? "answer" : "solution"} /></Preview>
    </Field>)}</Fields>
    <Metadata>
      <label>難度<select aria-label="變式難度" value={form.difficulty} disabled={saving} onChange={(event) => change("difficulty", event.target.value)}>{questionDifficulties.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
      <label>變化摘要<textarea aria-label="變化摘要" rows={2} value={form.change_summary} disabled={saving} placeholder="例如：更換係數，答案由整數改為分數" onChange={(event) => change("change_summary", event.target.value)} /></label>
    </Metadata>
    <Review><input type="checkbox" checked={reviewed} disabled={busy || !complete} onChange={(event) => setReviewed(event.target.checked)} />我已核對題幹、答案、詳解與難度</Review>
    {!complete && <Hint>「確認並使用」前，請填好題幹、答案與詳解。</Hint>}
    <Actions>
      <Button type="button" disabled={busy || !canSave || hasPromptImage} title={hasPromptImage ? "題幹含圖片，請連同題圖人工核對" : undefined} onClick={recalculate}>{aiBusy ? "計算中…" : "AI 重新計算"}</Button>
      <Button type="button" disabled={busy || !canSave} onClick={() => save(false)}>{saving ? "儲存中…" : savedStatus === "draft" ? "保存草稿" : "儲存修改"}</Button>
      <Button type="button" $primary disabled={busy || !canSave || !complete || !reviewed} onClick={() => save(true)}>確認並使用</Button>
    </Actions>
  </Editor>;
}

const Editor = styled.section`
  color: var(--app-surface-text, #243047);
  display: grid;
  gap: 16px;
  min-width: 0;
`;
const Header = styled.div`
  display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 12px;
  h3 { margin: 0 0 6px; font-size: 19px; }
  p { margin: 0; font-size: 13px; color: var(--app-muted-text, #64748b); }
`;
const Hint = styled.p`
  margin: 0; line-height: 1.65; font-size: 13px; color: var(--app-muted-text, #64748b);
`;
const Message = styled.div`
  border: 1px solid ${({ $error }) => $error ? "#ef4444" : "var(--app-border-color, #cbd5e1)"};
  border-radius: 10px; padding: 10px 12px; font-size: 14px; line-height: 1.6;
  color: ${({ $error }) => $error ? "var(--app-error-text, #dc2626)" : "inherit"};
`;
const Fields = styled.div`display: grid; gap: 14px;`;
const Field = styled.div`
  display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 14px;
  label { display: grid; gap: 7px; font-size: 14px; font-weight: 650; }
  textarea { width: 100%; box-sizing: border-box; resize: vertical; border: 1px solid var(--app-border-color, #cbd5e1); border-radius: 9px; padding: 10px; color: inherit; background: var(--app-input-bg, #fff); font: inherit; font-weight: 400; line-height: 1.6; }
  textarea:focus-visible { outline: 2px solid #3b82f6; outline-offset: 2px; }
  @media (max-width: 720px) { grid-template-columns: minmax(0, 1fr); }
`;
const Preview = styled.div`
  border-radius: 9px; border: 1px solid var(--app-border-color, #e2e8f0); padding: 12px; min-width: 0; overflow-wrap: anywhere;
  background: var(--app-question-card-bg, #f8fafc); line-height: 1.6;
  > strong { display: block; margin-bottom: 8px; font-size: 12px; color: var(--app-muted-text, #64748b); }
  > span { font-size: 13px; color: var(--app-muted-text, #64748b); }
`;
const Metadata = styled.div`
  display: grid; grid-template-columns: 160px minmax(0, 1fr); gap: 14px;
  label { display: grid; align-content: start; gap: 7px; font-size: 14px; font-weight: 650; }
  textarea, select { min-width: 0; width: 100%; box-sizing: border-box; border: 1px solid var(--app-border-color, #cbd5e1); border-radius: 9px; padding: 10px; background: var(--app-input-bg, #fff); color: inherit; font: inherit; font-weight: 400; }
  textarea { resize: vertical; }
  textarea:focus-visible, select:focus-visible { outline: 2px solid #3b82f6; outline-offset: 2px; }
  @media (max-width: 600px) { grid-template-columns: minmax(0, 1fr); }
`;
const Review = styled.label`
  display: flex; align-items: flex-start; gap: 9px; font-size: 14px; line-height: 1.6;
  input { margin-top: 5px; accent-color: #2563eb; }
`;
const Actions = styled.div`display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 10px;`;
const Button = styled.button`
  border: 1px solid ${({ $primary }) => $primary ? "#2563eb" : "var(--app-border-color, #cbd5e1)"};
  border-radius: 9px; padding: 9px 14px; min-height: 40px; font: inherit; font-size: 14px; font-weight: 650;
  background: ${({ $primary }) => $primary ? "#2563eb" : "var(--app-input-bg, #fff)"};
  color: ${({ $primary }) => $primary ? "#fff" : "inherit"}; cursor: pointer;
  &:disabled { opacity: .5; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid #3b82f6; outline-offset: 3px; }
`;
